import { useNavigate } from 'react-router-dom';
import { useCharacterCreationStore, type CharacterDraft } from '../stores/characterCreationStore';
import { usePlaySessionStore } from '../stores/playSessionStore';
import { generateRandomCharacter } from '../utils/randomCharacter';
import type { RuntimeState } from '../utils/characterExport';

/**
 * Hook for applying character drafts to the store.
 * Consolidates all logic for applying random/imported characters.
 */
export function useApplyCharacterDraft() {
  const store = useCharacterCreationStore();
  const playSessionStore = usePlaySessionStore();
  const navigate = useNavigate();

  /**
   * Apply class-level specific choices (subclass, fighting style, expertise, ASI, class features)
   */
  const applyClassLevelChoices = (
    classId: string,
    classLevel: NonNullable<CharacterDraft['classLevels']>[number],
  ) => {
    // Subclass
    if (classLevel.subclassId) {
      store.setSubclass(classId, classLevel.subclassId);
    }

    // Fighting style (Fighter, Paladin, Ranger)
    if (classLevel.fightingStyleId) {
      store.setFightingStyle(classId, classLevel.fightingStyleId);
    }

    // Expertise (Rogue, Bard)
    if (classLevel.expertiseSkills?.length) {
      for (const skill of classLevel.expertiseSkills) {
        store.toggleExpertise(classId, skill);
      }
    }

    // ASI/Feat choices
    if (classLevel.asiChoices && Object.keys(classLevel.asiChoices).length > 0) {
      for (const [levelStr, choice] of Object.entries(classLevel.asiChoices)) {
        store.setAsiChoice(classId, parseInt(levelStr, 10), choice);
      }
    }

    // Phase 3: Class-specific features

    // Pact Boon (Warlock) - must be set before invocations for prereq checking
    if (classLevel.pactBoonId) {
      store.setPactBoon(classId, classLevel.pactBoonId);
    }

    // Eldritch Invocations (Warlock)
    if (classLevel.eldritchInvocations?.length) {
      for (const invocation of classLevel.eldritchInvocations) {
        store.toggleEldritchInvocation(classId, invocation);
      }
    }

    // Metamagic (Sorcerer)
    if (classLevel.metamagicChoices?.length) {
      for (const metamagic of classLevel.metamagicChoices) {
        store.toggleMetamagic(classId, metamagic);
      }
    }

    // Favored Enemies (Ranger)
    if (classLevel.favoredEnemies?.length) {
      for (const enemy of classLevel.favoredEnemies) {
        store.toggleFavoredEnemy(classId, enemy);
      }
    }

    // Natural Explorer Terrains (Ranger)
    if (classLevel.naturalExplorerTerrains?.length) {
      for (const terrain of classLevel.naturalExplorerTerrains) {
        store.toggleNaturalExplorerTerrain(classId, terrain);
      }
    }
  };

  /**
   * Apply a partial character draft to the store
   */
  const applyDraft = (draft: Partial<CharacterDraft>, rulesetId: string) => {
    store.reset();
    store.setRulesetId(rulesetId);

    // Core fields
    if (draft.name) store.setName(draft.name);
    if (draft.raceId !== undefined) store.setRace(draft.raceId);
    if (draft.subraceId) store.setSubrace(draft.subraceId);
    if (draft.backgroundId !== undefined) store.setBackground(draft.backgroundId);
    if (draft.abilityScoreMethod) store.setAbilityScoreMethod(draft.abilityScoreMethod);
    if (draft.abilityScores) store.setAbilityScores(draft.abilityScores);
    if (draft.alignment) store.setAlignment(draft.alignment);
    if (draft.equipmentSelections) store.setEquipmentChoices(draft.equipmentSelections);
    if (draft.selectedCantrips) store.setSelectedCantrips(draft.selectedCantrips);
    if (draft.knownSpells) store.setKnownSpells(draft.knownSpells);
    if (draft.preparedSpells) store.setPreparedSpells(draft.preparedSpells);

    // Handle class levels (supports multiclass)
    const classLevels = draft.classLevels;
    if (classLevels && classLevels.length > 0) {
      // Primary class: use setClass which resets classLevels and creates first entry
      const primaryClassLevel = classLevels[0];
      store.setClass(primaryClassLevel.classId);
      store.setSkillProficiencyChoices(primaryClassLevel.skillProficiencyChoices);

      // Set primary class to its target level (setClass creates at level 1)
      // incrementClassLevel adds 1 each time, so we need level - 1 increments
      for (let i = 1; i < primaryClassLevel.level; i++) {
        store.incrementClassLevel(primaryClassLevel.classId);
      }

      // Apply primary class choices
      applyClassLevelChoices(primaryClassLevel.classId, primaryClassLevel);

      // Secondary classes: use addClassLevel for each additional class
      for (let i = 1; i < classLevels.length; i++) {
        const classLevel = classLevels[i];

        // addClassLevel creates at level 1, then increment to target level
        store.addClassLevel(classLevel.classId, classLevel.skillProficiencyChoices);

        // Increment to target level (addClassLevel starts at 1)
        for (let lvl = 1; lvl < classLevel.level; lvl++) {
          store.incrementClassLevel(classLevel.classId);
        }

        // Apply class-specific choices
        applyClassLevelChoices(classLevel.classId, classLevel);
      }
    } else if (draft.classId !== undefined) {
      // Legacy: single classId without classLevels
      store.setClass(draft.classId);
      if (draft.skillProficiencyChoices) {
        store.setSkillProficiencyChoices(draft.skillProficiencyChoices);
      }
      if (draft.characterLevel && draft.characterLevel > 1) {
        store.setCharacterLevel(draft.characterLevel);
      }
    }

    // Personality and details
    if (draft.personalityTraits) store.setPersonalityTraits(draft.personalityTraits);
    if (draft.ideals) store.setIdeals(draft.ideals);
    if (draft.bonds) store.setBonds(draft.bonds);
    if (draft.flaws) store.setFlaws(draft.flaws);
    if (draft.backstory) store.setBackstory(draft.backstory);
    if (draft.appearance) store.setAppearance(draft.appearance);
    if (draft.currency) store.setCurrency(draft.currency);
  };

  /**
   * Generate and apply a random character, then navigate to the creator
   */
  const applyRandomCharacter = (rulesetId: string) => {
    const randomDraft = generateRandomCharacter(rulesetId);
    applyDraft(randomDraft, rulesetId);
    navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
  };

  /**
   * Apply an imported character and navigate to the creator.
   * If runtime state is provided, initializes a play session with that state.
   */
  const applyImportedCharacter = (
    imported: CharacterDraft,
    rulesetId: string,
    runtimeState?: RuntimeState,
  ) => {
    applyDraft(imported, rulesetId);

    // If runtime state was included, restore it to the play session
    if (runtimeState && imported.id) {
      // Initialize session with the runtime state's max values (using character UUID)
      playSessionStore.initializeSession(
        imported.id,
        runtimeState.hitPoints.max,
        runtimeState.spellSlots,
        runtimeState.hitDice,
        runtimeState.classResources,
      );

      // Then restore the current state (used slots, current HP, etc.)
      playSessionStore.restoreRuntimeState(runtimeState);
    }

    navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
  };

  return {
    applyDraft,
    applyRandomCharacter,
    applyImportedCharacter,
  };
}
